/**
 * Écrans du défi streamé (31/10/2026) : l'accord avant de filmer, l'aperçu de la caméra (en haut à gauche,
 * déplaçable) avec « Couper », et, sous la note, arrêter / annuler la vidéo, la revoir, l'enregistrer sur l'appareil,
 * ou la publier avec un titre comme défi streamé (puis la partager : `ChallengePublishToast`).
 */
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Download, Move, Play, Radio, Square, Trash2, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { startBackgroundPublish } from '@/lib/challenges/background-publish';
import type { RecordedStream } from '@/lib/challenges/exercise-recorder';

/** « 1:05 ». PUR. */
export function streamClock(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Titre proposé pour le défi streamé. PUR. */
export function defaultStreamTitle(exerciseTitle: string): string {
  const base = exerciseTitle.replace(/\s+/g, ' ').trim();
  return `Regarde-moi faire : ${base}`.slice(0, 120);
}

export function StreamConsentDialog({
  open,
  onAccept,
  onCancel,
}: {
  open: boolean;
  onAccept: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stream-consent-title"
        onClick={(e) => e.stopPropagation()}
        className="ocean:bg-[#061433] w-full max-w-md rounded-t-2xl bg-white p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] shadow-xl sm:rounded-2xl"
      >
        <h2 id="stream-consent-title" className="font-display text-ink-900 text-lg font-bold">
          Te filmer pendant l’exercice ?
        </h2>
        <p className="text-ink-600 mt-2 text-sm leading-relaxed">
          La caméra et le micro enregistrent jusqu’à ta note. La vidéo reste sur ton appareil : à la
          fin, tu choisis de la revoir, de l’enregistrer ou de la publier comme défi. Tu peux couper
          à tout moment.
        </p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" fullWidth onClick={onCancel}>
            Pas maintenant
          </Button>
          <Button type="button" variant="primary" fullWidth onClick={onAccept}>
            <Radio className="h-4 w-4" aria-hidden /> Commencer à filmer
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Garde l'aperçu dans l'écran (marge de 8 px). PUR. */
export function clampPreviewOffset(
  offset: { x: number; y: number },
  base: { left: number; top: number; width: number; height: number },
  viewport: { width: number; height: number },
): { x: number; y: number } {
  const margin = 8;
  const left = Math.min(
    Math.max(base.left + offset.x, margin),
    Math.max(margin, viewport.width - base.width - margin),
  );
  const top = Math.min(
    Math.max(base.top + offset.y, margin),
    Math.max(margin, viewport.height - base.height - margin),
  );
  return { x: left - base.left, y: top - base.top };
}

/**
 * Aperçu de la caméra pendant l'enregistrement : en haut à gauche, sous la barre de l'exercice, et DÉPLAÇABLE au
 * doigt ou à la souris (on le pose où il ne gêne pas). Point rouge, compteur, « Couper ».
 */
export function StreamPreview({
  stream,
  elapsed,
  onStop,
}: {
  stream: MediaStream;
  elapsed: () => number;
  onStop: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [, tick] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{
    id: number;
    px: number;
    py: number;
    start: { x: number; y: number };
    base: { left: number; top: number; width: number; height: number };
  } | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v && v.srcObject !== stream) {
      v.srcObject = stream;
      void v.play().catch(() => undefined);
    }
  }, [stream]);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  // l'écran tourne ou change de taille : l'aperçu revient à sa place
  useEffect(() => {
    const reset = () => setOffset({ x: 0, y: 0 });
    window.addEventListener('resize', reset);
    return () => window.removeEventListener('resize', reset);
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    drag.current = {
      id: e.pointerId,
      px: e.clientX,
      py: e.clientY,
      start: offset,
      // la place d'origine (sans le décalage) : le décalage se calcule à partir d'elle
      base: { left: r.left - offset.x, top: r.top - offset.y, width: r.width, height: r.height },
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setOffset(
      clampPreviewOffset(
        { x: d.start.x + e.clientX - d.px, y: d.start.y + e.clientY - d.py },
        d.base,
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  return createPortal(
    <div
      ref={boxRef}
      className="fixed left-3 z-[65] w-24 overflow-hidden rounded-2xl bg-black shadow-lg ring-2 ring-red-500 sm:w-32"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)',
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
      }}
      data-testid="stream-preview"
    >
      <div
        className="relative cursor-grab touch-none select-none active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        title="Glisse pour déplacer"
        data-testid="stream-preview-drag"
      >
        <video
          ref={videoRef}
          muted
          playsInline
          className="pointer-events-none aspect-[3/4] w-full -scale-x-100 object-cover"
          aria-label="Ta caméra"
        />
        <div className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-xs font-semibold text-white">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden />
          {streamClock(elapsed())}
        </div>
        <Move
          className="absolute right-1.5 top-1.5 h-4 w-4 text-white/80 drop-shadow"
          aria-hidden
        />
      </div>
      <button
        type="button"
        onClick={onStop}
        className="flex min-h-11 w-full items-center justify-center gap-1 bg-red-600 text-sm font-semibold text-white active:scale-[0.98]"
      >
        <Square className="h-3.5 w-3.5 fill-white" aria-hidden /> Couper
      </button>
    </div>,
    document.body,
  );
}

/** Sur l'écran de note, la caméra tourne encore (elle s'arrête seule 5 s après) : l'arrêter tout de suite ou annuler. */
export function StreamRecordingActions({
  elapsed,
  onStop,
  onDiscard,
}: {
  elapsed: () => number;
  onStop: () => void;
  onDiscard: () => void;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <section
      className="ocean:border-white/10 ocean:bg-white/5 space-y-3 rounded-2xl border border-red-200 bg-red-50/60 p-4"
      aria-label="Ta vidéo"
      data-testid="stream-recording-actions"
    >
      <p className="text-ink-900 flex items-center gap-2 text-sm font-semibold">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden />
        La caméra tourne encore · {streamClock(elapsed())}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button type="button" variant="primary" fullWidth onClick={onStop}>
          <Square className="h-4 w-4" aria-hidden /> Arrêter et publier
        </Button>
        <Button type="button" variant="outline" fullWidth onClick={onDiscard}>
          <Trash2 className="h-4 w-4" aria-hidden /> Annuler la vidéo
        </Button>
      </div>
    </section>
  );
}

/**
 * Sous la note, la vidéo finie : son titre, « Publier et partager » (défi streamé), revoir, enregistrer sur
 * l'appareil, ou l'annuler. « Continuer » (au-dessus) n'apparaît qu'une fois publiée ou annulée.
 */
export function StreamResultActions({
  result,
  exerciseId,
  exerciseTitle,
  attemptId,
  userId,
  published,
  onPublished,
  onDiscard,
}: {
  result: RecordedStream & { url: string };
  exerciseId: string;
  exerciseTitle: string;
  attemptId: string;
  userId: string;
  published: boolean;
  onPublished: () => void;
  onDiscard: () => void;
}) {
  const [watching, setWatching] = useState(false);
  const [title, setTitle] = useState(() => defaultStreamTitle(exerciseTitle));
  const fileName = `jeraisonne-exercice.${result.ext}`;

  const save = async () => {
    const file = new File([result.blob], fileName, { type: result.contentType });
    // téléphone : la feuille de partage propose « Enregistrer la vidéo »
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'Ma vidéo JeRaisonne' });
        return;
      } catch {
        /* annulé : on télécharge */
      }
    }
    const a = document.createElement('a');
    a.href = result.url;
    a.download = fileName;
    a.click();
  };

  const publish = () => {
    startBackgroundPublish({
      payload: {
        exerciseId,
        attemptId,
        stepId: null,
        blockId: null,
        shareType: 'stream',
        title: title.trim() || defaultStreamTitle(exerciseTitle),
        mediaType: 'video',
        streamTimeline: result.timeline,
      },
      mediaBlob: result.blob,
      userId,
      videoMimeOverride: { contentType: result.contentType, ext: result.ext },
    });
    onPublished();
  };

  const discard = () => {
    if (window.confirm('Annuler la vidéo ? Elle sera effacée de ton appareil.')) onDiscard();
  };

  return (
    <section
      className="ocean:border-white/10 ocean:bg-white/5 space-y-3 rounded-2xl border border-red-200 bg-red-50/60 p-4"
      aria-label="Ta vidéo"
      data-testid="stream-actions"
    >
      <p className="text-ink-900 flex items-center gap-2 text-sm font-semibold">
        <Radio className="h-4 w-4 text-red-600" aria-hidden />
        Ta vidéo · {streamClock(result.durationMs)}
      </p>
      {published ? (
        <p className="text-ink-700 text-sm" role="status">
          Ton défi part en publication : tu pourras le partager dès qu’il est en ligne.
        </p>
      ) : (
        <div className="space-y-2">
          <label htmlFor="stream-title" className="text-ink-700 block text-sm font-semibold">
            Titre du défi
          </label>
          <input
            id="stream-title"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            className="border-ink-200 text-ink-900 block min-h-11 w-full rounded-xl border bg-white px-3 text-base"
          />
          <p className="text-ink-500 text-xs">
            Ceux qui l’ouvrent te voient en haut et l’exercice s’enchaîner en bas.
          </p>
          <Button type="button" variant="primary" fullWidth onClick={publish}>
            <Radio className="h-4 w-4" aria-hidden /> Publier et partager
          </Button>
        </div>
      )}
      <div className={`grid grid-cols-1 gap-2 ${published ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
        <Button type="button" variant="outline" fullWidth onClick={() => setWatching(true)}>
          <Play className="h-4 w-4" aria-hidden /> Revoir
        </Button>
        <Button type="button" variant="outline" fullWidth onClick={() => void save()}>
          <Download className="h-4 w-4" aria-hidden /> Enregistrer
        </Button>
        {!published && (
          <Button type="button" variant="outline" fullWidth onClick={discard}>
            <Trash2 className="h-4 w-4" aria-hidden /> Annuler la vidéo
          </Button>
        )}
      </div>
      {watching &&
        createPortal(
          <div
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setWatching(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Ta vidéo"
              className="relative w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={result.url}
                controls
                autoPlay
                playsInline
                className="w-full rounded-2xl"
              />
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => setWatching(false)}
                className="absolute -top-12 right-0 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
